import {
  AdminFetchPaymentConfig,
  AdminSavePayoutSettings,
  AdminSaveVendorFees,
} from 'src/constants/actionTypes'
import type { PaymentConfig } from 'src/interfaces/payment'
import type { Action } from 'src/utils/reduxUtils'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import type { PaymentConfigState } from './types'

const getInitialState = (): PaymentConfigState => {
  return {
    partnerId: null,
    config: null,
    loading: false,
    loaded: false,
    error: null,
    savingPayoutSettings: false,
    savingVendorFees: false,
  }
}

/**
 * Neither save writes `config`. The PUT echo is partial — TypeORM's `save({ id, ...data })`
 * returns the object it was handed, not a reload — so a vendor-fees save would otherwise blank
 * the payout settings held in state. Both save sagas chain a fresh fetch instead, and
 * `AdminFetchPaymentConfig.success` is the only case that assigns `config`.
 */
export const paymentConfigReducer = (
  state: PaymentConfigState = getInitialState(),
  action: Action<unknown>,
): PaymentConfigState => {
  switch (action.type) {
    case AdminFetchPaymentConfig.type.REQUEST: {
      const partnerId = (action.payload as { partnerId?: string } | undefined)?.partnerId
      const isSamePartner = Boolean(partnerId) && state.partnerId === partnerId

      return {
        ...state,
        partnerId: partnerId ?? state.partnerId,
        config: isSamePartner ? state.config : null,
        loaded: isSamePartner ? state.loaded : false,
        loading: true,
        error: null,
      }
    }
    case AdminFetchPaymentConfig.type.SUCCESS: {
      const payload = action.payload as
        | { partnerId: string; config: PaymentConfig | null }
        | undefined

      if (payload === undefined || payload.partnerId !== state.partnerId) {
        return state
      }

      return {
        ...state,
        config: payload.config,
        loading: false,
        loaded: true,
        error: null,
      }
    }
    case AdminFetchPaymentConfig.type.FAILED:
      return {
        ...state,
        loading: false,
        error: resolveErrorMessage(action.payload, 'Failed to load payment configuration'),
      }

    case AdminSavePayoutSettings.type.REQUEST:
      return {
        ...state,
        savingPayoutSettings: true,
      }
    case AdminSavePayoutSettings.type.SUCCESS:
    case AdminSavePayoutSettings.type.FAILED:
      return {
        ...state,
        savingPayoutSettings: false,
      }

    case AdminSaveVendorFees.type.REQUEST:
      return {
        ...state,
        savingVendorFees: true,
      }
    case AdminSaveVendorFees.type.SUCCESS:
    case AdminSaveVendorFees.type.FAILED:
      return {
        ...state,
        savingVendorFees: false,
      }

    default:
      return state
  }
}
